import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, Calendar, DollarSign, Clock, 
  Star, UserCheck, UserX, Activity, RefreshCw, ArrowUp, ArrowDown,
  CheckCircle, XCircle, AlertTriangle, Percent, Download, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

const AnalyticsDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [patientInsights, setPatientInsights] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [topServicesSortBy, setTopServicesSortBy] = useState('count'); // 'count' or 'revenue'
  const [showAllServices, setShowAllServices] = useState(false);
  
  // Custom date range for export
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Therapist detail view state
  const [expandedTherapist, setExpandedTherapist] = useState(null);
  const [excludedServices, setExcludedServices] = useState({}); // { therapistName: [serviceName1, serviceName2] }
  const [therapistDateRange, setTherapistDateRange] = useState({ start: '', end: '' });
  const [useCustomTherapistDates, setUseCustomTherapistDates] = useState(false);

  // Top spenders state
  const [topSpendersMode, setTopSpendersMode] = useState('lifetime'); // 'lifetime' | 'range'
  const [topSpenders, setTopSpenders] = useState([]);
  const [topSpendersLoading, setTopSpendersLoading] = useState(false);

  // Calculate actual start and end dates
  const getDateRangeValues = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { startDate, endDate };
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeValues();
      
      const [dashboardRes, patientRes] = await Promise.all([
        api.get(`/analytics/dashboard?start_date=${startDate}&end_date=${endDate}`),
        api.get(`/analytics/patient-insights?start_date=${startDate}&end_date=${endDate}`)
      ]);
      
      setData(dashboardRes.data);
      setPatientInsights(patientRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
      console.error(error);
    }
    setLoading(false);
  };

  // Top spenders fetcher — reruns when toggle or range changes
  useEffect(() => {
    const load = async () => {
      setTopSpendersLoading(true);
      try {
        const params = new URLSearchParams({ mode: topSpendersMode, limit: '50' });
        if (topSpendersMode === 'range') {
          const { startDate, endDate } = getDateRangeValues();
          params.set('start_date', startDate);
          params.set('end_date', endDate);
        }
        const res = await api.get(`/analytics/top-spenders?${params.toString()}`);
        setTopSpenders(res.data?.rows || []);
      } catch {
        toast.error('Could not load top spenders');
      } finally {
        setTopSpendersLoading(false);
      }
    };
    load();
  }, [topSpendersMode, dateRange]);

  // Export therapist revenue CSV
  const handleExportTherapistCSV = (useCustomDates = false) => {
    const token = localStorage.getItem('token');
    let startDate, endDate;
    
    if (useCustomDates && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      const dates = getDateRangeValues();
      startDate = dates.startDate;
      endDate = dates.endDate;
    }
    
    const url = `${API_URL}/api/analytics/therapist-revenue/export?start_date=${startDate}&end_date=${endDate}`;
    
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `therapist_revenue_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV exported successfully');
      setShowCustomDatePicker(false);
    })
    .catch(err => {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    });
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const { summary, appointment_status, revenue_by_method, trends, top_services, all_services,
          rating_distribution, therapist_performance, peak_hours, weekly_distribution } = data;

  // Calculate comparison indicators (simplified - comparing to period average)
  const avgDailyRevenue = summary.total_revenue / parseInt(dateRange);
  const avgDailyAppointments = summary.total_appointments / parseInt(dateRange);

  // Sort services based on selected criteria
  const servicesToDisplay = showAllServices ? (all_services || top_services) : top_services;
  const sortedServices = [...servicesToDisplay].sort((a, b) => {
    if (topServicesSortBy === 'revenue') {
      return b.revenue - a.revenue;
    }
    return b.count - a.count;
  });

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500">
            Insights for {data.period.start_date} to {data.period.end_date}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="60">Last 60 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Calendar className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{summary.total_appointments}</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Total Appointments</p>
            <p className="text-xs opacity-70">{avgDailyAppointments.toFixed(1)}/day avg</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <DollarSign className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">₹{(summary.total_revenue/1000).toFixed(1)}K</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Total Revenue</p>
            <p className="text-xs opacity-70">₹{avgDailyRevenue.toFixed(0)}/day avg</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{summary.unique_patients}</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Unique Patients</p>
            <p className="text-xs opacity-70">{summary.new_patients} new</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Percent className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{summary.completion_rate}%</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Completion Rate</p>
            <p className="text-xs opacity-70">{summary.completed_appointments} completed</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Star className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{summary.avg_rating || 'N/A'}</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Avg Rating</p>
            <p className="text-xs opacity-70">{summary.total_reviews} reviews</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Clock className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{summary.avg_service_duration}m</span>
            </div>
            <p className="text-sm mt-2 opacity-90">Avg Service Time</p>
            <p className="text-xs opacity-70">{summary.avg_lateness_minutes}m avg late</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.completed_appointments}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{appointment_status.cancelled}</p>
              <p className="text-sm text-gray-500">Cancelled ({summary.cancellation_rate}%)</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <UserX className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{appointment_status.no_show}</p>
              <p className="text-sm text-gray-500">No Shows ({summary.no_show_rate}%)</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">₹{summary.avg_transaction}</p>
              <p className="text-sm text-gray-500">Avg Transaction</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Appointments Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Revenue & Appointments Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorAppointments" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [name === 'revenue' ? `₹${value}` : value, name === 'revenue' ? 'Revenue' : 'Appointments']}
                />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#8b5cf6" fill="url(#colorRevenue)" name="Revenue" />
                <Area yAxisId="right" type="monotone" dataKey="appointments" stroke="#06b6d4" fill="url(#colorAppointments)" name="Appointments" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Appointment Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-600" />
              Appointment Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Completed', value: appointment_status.completed, color: '#10b981' },
                    { name: 'Confirmed', value: appointment_status.confirmed, color: '#06b6d4' },
                    { name: 'Checked In', value: appointment_status.checked_in, color: '#8b5cf6' },
                    { name: 'Cancelled', value: appointment_status.cancelled, color: '#ef4444' },
                    { name: 'No Show', value: appointment_status.no_show, color: '#f59e0b' }
                  ].filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {[
                    { name: 'Completed', value: appointment_status.completed, color: '#10b981' },
                    { name: 'Confirmed', value: appointment_status.confirmed, color: '#06b6d4' },
                    { name: 'Checked In', value: appointment_status.checked_in, color: '#8b5cf6' },
                    { name: 'Cancelled', value: appointment_status.cancelled, color: '#ef4444' },
                    { name: 'No Show', value: appointment_status.no_show, color: '#f59e0b' }
                  ].filter(d => d.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Count']} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Services with Sort Toggle */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{showAllServices ? 'All Services' : 'Top Services'} (Completed)</CardTitle>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setTopServicesSortBy('count')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    topServicesSortBy === 'count' 
                      ? 'bg-white text-purple-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  By Count
                </button>
                <button
                  onClick={() => setTopServicesSortBy('revenue')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    topServicesSortBy === 'revenue' 
                      ? 'bg-white text-green-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  By Value
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className={`space-y-3 ${showAllServices ? 'max-h-[300px] overflow-y-auto pr-2' : ''}`}>
              {(showAllServices ? sortedServices : sortedServices.slice(0, 6)).map((service, index) => (
                <div key={service.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      topServicesSortBy === 'revenue' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-purple-100 text-purple-600'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]" title={service.name}>
                      {service.name}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${topServicesSortBy === 'count' ? 'text-purple-600' : 'text-gray-900'}`}>
                      {service.count} completed
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {service.appointments || 0} appts • {service.walk_ins || 0} walk-ins
                    </div>
                    <div className={`text-xs ${topServicesSortBy === 'revenue' ? 'text-green-600 font-semibold' : 'text-green-600'}`}>
                      ₹{service.revenue.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* View All / View Less Toggle */}
            {(all_services?.length > 6 || showAllServices) && (
              <button
                onClick={() => setShowAllServices(!showAllServices)}
                className="mt-3 w-full text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium py-2 border-t border-gray-100"
              >
                {showAllServices ? `Show Top 6 Only` : `View All ${all_services?.length || top_services.length} Services`}
              </button>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Payment Method */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by Payment Method</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={revenue_by_method}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="amount"
                  nameKey="method"
                  label={({ method, percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {revenue_by_method.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Rating Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Rating Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={rating_distribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="rating" type="category" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v} ⭐`} />
                <Tooltip />
                <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Peak Hours Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={peak_hours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Appointments" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Weekly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Weekly Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={weekly_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Appointments" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Therapist Performance & Patient Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Therapist Performance with Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-600" />
              Therapist Performance & Revenue
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                className="text-gray-600 border-gray-200 hover:bg-gray-50"
              >
                <Calendar className="w-4 h-4 mr-1" />
                Custom
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportTherapistCSV(false)}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                data-testid="export-therapist-csv"
              >
                <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Custom Date Picker for Analytics */}
            {showCustomDatePicker && (
              <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-900 mb-3">Select Custom Date Range</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs text-purple-700 block mb-1">From</label>
                    <input
                      type="date"
                      value={therapistDateRange.start || customStartDate}
                      onChange={(e) => {
                        setTherapistDateRange(prev => ({ ...prev, start: e.target.value }));
                        setCustomStartDate(e.target.value);
                      }}
                      className="px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-purple-700 block mb-1">To</label>
                    <input
                      type="date"
                      value={therapistDateRange.end || customEndDate}
                      onChange={(e) => {
                        setTherapistDateRange(prev => ({ ...prev, end: e.target.value }));
                        setCustomEndDate(e.target.value);
                      }}
                      className="px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (therapistDateRange.start && therapistDateRange.end) {
                        setUseCustomTherapistDates(true);
                        fetchAnalytics();
                      }
                    }}
                    disabled={!therapistDateRange.start || !therapistDateRange.end}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Apply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExportTherapistCSV(true)}
                    disabled={!therapistDateRange.start || !therapistDateRange.end}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            )}
            
            {therapist_performance.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No therapist data available</p>
            ) : (
              <div className="space-y-3">
                {therapist_performance.slice(0, 8).map((therapist, index) => {
                  const isExpanded = expandedTherapist === therapist.name;
                  const serviceBreakdown = data.therapist_service_breakdown?.[therapist.name] || [];
                  const excludedForTherapist = excludedServices[therapist.name] || [];
                  
                  // Calculate filtered revenue
                  const filteredServices = serviceBreakdown.filter(
                    svc => !excludedForTherapist.includes(svc.service)
                  );
                  const filteredRevenue = filteredServices.reduce((sum, svc) => sum + svc.revenue, 0);
                  const filteredCount = filteredServices.reduce((sum, svc) => sum + svc.count, 0);
                  
                  return (
                    <div key={therapist.name} className="bg-gray-50 rounded-lg overflow-hidden">
                      {/* Therapist Header - Clickable */}
                      <div 
                        className="p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => setExpandedTherapist(isExpanded ? null : therapist.name)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </span>
                            <p className="font-medium text-gray-900 flex items-center gap-2">
                              {therapist.name}
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </p>
                          </div>
                          <p className="text-lg font-bold text-green-600">
                            ₹{(excludedForTherapist.length > 0 ? filteredRevenue : therapist.revenue)?.toLocaleString() || 0}
                          </p>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {excludedForTherapist.length > 0 ? filteredCount : therapist.completed} procedures
                            {excludedForTherapist.length > 0 && (
                              <span className="text-amber-600 ml-1">
                                ({excludedForTherapist.length} excluded)
                              </span>
                            )}
                          </span>
                          <span className="text-purple-600">Click to view details</span>
                        </div>
                        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-green-500 rounded-full"
                            style={{ width: `${Math.min(((excludedForTherapist.length > 0 ? filteredRevenue : therapist.revenue) / (therapist_performance[0]?.revenue || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Expanded Service Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-white p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-gray-700 text-sm">Services Breakdown</h4>
                            {excludedForTherapist.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExcludedServices(prev => ({
                                    ...prev,
                                    [therapist.name]: []
                                  }));
                                }}
                                className="text-xs text-purple-600 h-7"
                              >
                                Reset All
                              </Button>
                            )}
                          </div>
                          
                          {serviceBreakdown.length === 0 ? (
                            <p className="text-sm text-gray-500">No service data available</p>
                          ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {serviceBreakdown.map((svc) => {
                                const isExcluded = excludedForTherapist.includes(svc.service);
                                return (
                                  <div 
                                    key={svc.service}
                                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                      isExcluded ? 'bg-gray-100 opacity-60' : 'bg-purple-50'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={!isExcluded}
                                      onCheckedChange={(checked) => {
                                        setExcludedServices(prev => {
                                          const current = prev[therapist.name] || [];
                                          if (checked) {
                                            // Remove from excluded
                                            return {
                                              ...prev,
                                              [therapist.name]: current.filter(s => s !== svc.service)
                                            };
                                          } else {
                                            // Add to excluded
                                            return {
                                              ...prev,
                                              [therapist.name]: [...current, svc.service]
                                            };
                                          }
                                        });
                                      }}
                                      className="data-[state=checked]:bg-purple-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium truncate ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                        {svc.service}
                                      </p>
                                      <p className="text-xs text-gray-500">{svc.count} procedures</p>
                                    </div>
                                    <p className={`text-sm font-bold ${isExcluded ? 'line-through text-gray-400' : 'text-green-600'}`}>
                                      ₹{svc.revenue.toLocaleString()}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Selected Total */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">
                                Selected Total ({filteredServices.length} services)
                              </span>
                              <span className="text-lg font-bold text-green-600">
                                ₹{filteredRevenue.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Total Revenue by all therapists */}
                <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-purple-900">Total Therapist Revenue</span>
                    <span className="text-xl font-bold text-purple-700">
                      ₹{(() => {
                        // Calculate total considering excluded services
                        let total = 0;
                        therapist_performance.forEach(t => {
                          const excluded = excludedServices[t.name] || [];
                          if (excluded.length > 0) {
                            const breakdown = data.therapist_service_breakdown?.[t.name] || [];
                            const filtered = breakdown.filter(s => !excluded.includes(s.service));
                            total += filtered.reduce((sum, s) => sum + s.revenue, 0);
                          } else {
                            total += t.revenue || 0;
                          }
                        });
                        return total.toLocaleString();
                      })()}
                    </span>
                  </div>
                  {Object.values(excludedServices).some(arr => arr.length > 0) && (
                    <p className="text-xs text-purple-600 mt-1">
                      * Some services excluded from calculation
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Patient Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Patient Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            {patientInsights && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Total Patients</p>
                    <p className="text-xl font-bold text-purple-700">{patientInsights.total_patients}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active in Period</p>
                    <p className="text-xl font-bold text-purple-700">{patientInsights.active_patients}</p>
                  </div>
                </div>
                
                {patientInsights.top_patients.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Top Visitors</p>
                    <div className="space-y-2">
                      {patientInsights.top_patients.slice(0, 3).map((patient, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{patient.name}</span>
                          <Badge variant="outline">{patient.visits} visits</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {patientInsights.frequent_no_shows.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-2">Frequent No-Shows</p>
                    <div className="space-y-2">
                      {patientInsights.frequent_no_shows.slice(0, 3).map((patient, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{patient.name}</span>
                          <Badge className="bg-red-100 text-red-700">{patient.no_show_count} no-shows</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Service-wise Patient Breakdown (New vs Old) */}
      {data?.service_patient_breakdown && data.service_patient_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Service-wise Patient Breakdown (New vs Returning)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-semibold text-gray-700">Service</th>
                    <th className="text-center py-3 px-2 font-semibold text-green-700">New Patients</th>
                    <th className="text-center py-3 px-2 font-semibold text-blue-700">Returning</th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-700">Total Patients</th>
                    <th className="text-center py-3 px-2 font-semibold text-purple-700">Total Visits</th>
                    <th className="text-right py-3 px-2 font-semibold text-emerald-700">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.service_patient_breakdown.map((service, index) => (
                    <tr 
                      key={service.service} 
                      className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <td className="py-3 px-2 font-medium text-gray-900">{service.service}</td>
                      <td className="text-center py-3 px-2">
                        <span className="inline-flex items-center justify-center w-10 h-7 bg-green-100 text-green-700 rounded-full font-semibold">
                          {service.new_patients}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="inline-flex items-center justify-center w-10 h-7 bg-blue-100 text-blue-700 rounded-full font-semibold">
                          {service.old_patients}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="font-semibold text-gray-700">{service.total_patients}</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="font-semibold text-purple-700">{service.total_visits}</span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="font-bold text-emerald-600">₹{service.revenue.toLocaleString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-semibold">
                    <td className="py-3 px-2 text-gray-900">Total</td>
                    <td className="text-center py-3 px-2 text-green-700">
                      {data.service_patient_breakdown.reduce((sum, s) => sum + s.new_patients, 0)}
                    </td>
                    <td className="text-center py-3 px-2 text-blue-700">
                      {data.service_patient_breakdown.reduce((sum, s) => sum + s.old_patients, 0)}
                    </td>
                    <td className="text-center py-3 px-2 text-gray-700">
                      {data.service_patient_breakdown.reduce((sum, s) => sum + s.total_patients, 0)}
                    </td>
                    <td className="text-center py-3 px-2 text-purple-700">
                      {data.service_patient_breakdown.reduce((sum, s) => sum + s.total_visits, 0)}
                    </td>
                    <td className="text-right py-3 px-2 text-emerald-700">
                      ₹{data.service_patient_breakdown.reduce((sum, s) => sum + s.revenue, 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts - Visual Trends Over Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New vs Returning Patients Trend */}
        {data?.daily_patient_trends && data.daily_patient_trends.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                New vs Returning Patients (Daily Trend)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.daily_patient_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                      }}
                      formatter={(value, name) => [value, name === 'new_patients' ? 'New Patients' : name === 'returning_patients' ? 'Returning' : name]}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="new_patients" 
                      name="New Patients"
                      stackId="1"
                      stroke="#10b981" 
                      fill="#d1fae5" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="returning_patients" 
                      name="Returning"
                      stackId="1"
                      stroke="#3b82f6" 
                      fill="#dbeafe" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Revenue Trend */}
        {data?.daily_patient_trends && data.daily_patient_trends.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Daily Revenue Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily_patient_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                      }}
                      formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Revenue"
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Revenue Bar Chart */}
        {data?.service_patient_breakdown && data.service_patient_breakdown.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Revenue by Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={data.service_patient_breakdown.slice(0, 8)} 
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      type="number" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="service" 
                      tick={{ fontSize: 10 }}
                      width={100}
                    />
                    <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
                    <Bar 
                      dataKey="revenue" 
                      name="Revenue"
                      fill="#8b5cf6" 
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* New vs Returning by Service */}
        {data?.service_patient_breakdown && data.service_patient_breakdown.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                New vs Returning by Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={data.service_patient_breakdown.slice(0, 8)} 
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis 
                      type="category" 
                      dataKey="service" 
                      tick={{ fontSize: 10 }}
                      width={100}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="new_patients" 
                      name="New"
                      fill="#10b981" 
                      stackId="patients"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar 
                      dataKey="old_patients" 
                      name="Returning"
                      fill="#3b82f6" 
                      stackId="patients"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Patient Tags Distribution */}
      {patientInsights && patientInsights.tag_distribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Patient Tags Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {patientInsights.tag_distribution.map((tag) => (
                <Badge key={tag.tag} variant="outline" className="px-3 py-1">
                  {tag.tag}: {tag.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Spenders */}
      <Card data-testid="top-spenders-card">
        <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <span className="text-amber-600">₹</span> Top 50 Spenders
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              {topSpendersMode === 'lifetime'
                ? 'Across all time, net of cancelled / refunded.'
                : `For selected range (${getDateRangeValues().startDate} → ${getDateRangeValues().endDate}).`}
            </p>
          </div>
          <div className="inline-flex rounded-xl bg-slate-100 p-1" role="group">
            <button
              data-testid="top-spenders-toggle-lifetime"
              onClick={() => setTopSpendersMode('lifetime')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${topSpendersMode === 'lifetime' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Lifetime
            </button>
            <button
              data-testid="top-spenders-toggle-range"
              onClick={() => setTopSpendersMode('range')}
              className={`px-3 py-1.5 text-sm rounded-lg transition ${topSpendersMode === 'range' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Range (last {dateRange} days)
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {topSpendersLoading ? (
            <div className="text-center py-8 text-slate-500">Loading…</div>
          ) : topSpenders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No paying patients in this view yet.</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Patient</th>
                    <th className="text-left px-3 py-2">Phone</th>
                    <th className="text-right px-3 py-2">{topSpendersMode === 'lifetime' ? 'Lifetime Value' : 'Net Spend'}</th>
                    <th className="text-right px-3 py-2">Paid</th>
                    <th className="text-right px-3 py-2">Refunded</th>
                    <th className="text-right px-3 py-2">Payments</th>
                    <th className="text-left px-3 py-2">Last Payment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topSpenders.map((r, i) => (
                    <tr key={r.patient_id} className="hover:bg-slate-50" data-testid={`top-spender-row-${r.patient_id}`}>
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{r.patient_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 font-mono text-xs">{r.patient_phone || '—'}</td>
                      <td className="px-3 py-2 text-right font-bold text-emerald-700">
                        ₹{(r.lifetime_value || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">₹{(r.total_paid || 0).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-rose-700">
                        {r.total_refunded ? `−₹${(r.total_refunded).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">{r.payment_count}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {r.last_payment_at ? new Date(r.last_payment_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
